import { useEffect, useRef, useState } from 'react'
import { BITHOMP_API_BASE } from './config'
import { fetchBithompTokens } from './tokenRegistry'
import { XrplCurrency } from './types'

const parseEnvTokens = (): XrplCurrency[] | null => {
  const raw = process.env.REACT_APP_XRPL_TOKENS
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as XrplCurrency[]
    return parsed
  } catch (error) {
    console.warn('Failed to parse REACT_APP_XRPL_TOKENS', error)
    return null
  }
}

const DEFAULT_BESC_ISSUER = process.env.REACT_APP_BESC_ISSUER || 'rUnjJv3iLPNWTAVXoNUfHD9CBm7tcwkjLa'
const DEFAULT_BESC_CURRENCY =
  process.env.REACT_APP_BESC_CURRENCY || '4245534300000000000000000000000000000000'
const DEFAULT_BESC_SYMBOL = process.env.REACT_APP_BESC_SYMBOL || 'BESC'
const DEFAULT_BESC_NAME = process.env.REACT_APP_BESC_NAME || 'BESC'
const DEFAULT_BESC_DECIMALS = Number(process.env.REACT_APP_BESC_DECIMALS || '6')
const DEFAULT_BESC_ICON = `https://cdn.bithomp.com/issued-token/${DEFAULT_BESC_ISSUER}/${DEFAULT_BESC_CURRENCY}`
export const DEFAULT_XRP_ICON = '/xrp-logo.webp'

const defaultTokens: XrplCurrency[] = [
  {
    currency: 'XRP',
    symbol: 'XRP',
    name: 'XRP',
    decimals: 6,
    isNative: true,
    icon: DEFAULT_XRP_ICON,
  },
  {
    currency: DEFAULT_BESC_CURRENCY,
    issuer: DEFAULT_BESC_ISSUER,
    symbol: DEFAULT_BESC_SYMBOL,
    name: DEFAULT_BESC_NAME,
    decimals: DEFAULT_BESC_DECIMALS,
    icon: DEFAULT_BESC_ICON,
  },
]

const envTokens = parseEnvTokens()
const CUSTOM_TOKENS_STORAGE_KEY = 'xrpl_custom_tokens'

const loadCustomTokens = (): XrplCurrency[] => {
  if (typeof window === 'undefined') return []
  const raw = window.localStorage.getItem(CUSTOM_TOKENS_STORAGE_KEY)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as XrplCurrency[]
    if (!Array.isArray(parsed)) return []
    return parsed.filter((token) => token && (token.isNative || token.currency === 'XRP' || Boolean(token.issuer)))
  } catch (error) {
    console.warn('Failed to parse custom XRPL tokens', error)
    return []
  }
}

const saveCustomTokens = (tokens: XrplCurrency[]) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(CUSTOM_TOKENS_STORAGE_KEY, JSON.stringify(tokens))
  } catch (error) {
    console.warn('Failed to store custom XRPL tokens', error)
  }
}

export const XRPL_TOKENS = (envTokens || defaultTokens).filter(
  (token) => token.isNative || token.currency === 'XRP' || Boolean(token.issuer),
)

export const getTokenKey = (token: XrplCurrency) => {
  if (token.isNative || token.currency === 'XRP') {
    return 'XRP'
  }
  return `${token.currency}:${token.issuer}`
}

export const getTokenLabel = (token: XrplCurrency) => {
  if (!token.issuer || token.isNative || token.currency === 'XRP') {
    return token.symbol
  }
  const issuer = token.issuer
  const shortIssuer = `${issuer.slice(0, 4)}...${issuer.slice(-4)}`
  return `${token.symbol} (${shortIssuer})`
}

export const getTokenByKey = (key: string, tokens = XRPL_TOKENS) =>
  tokens.find((token) => getTokenKey(token) === key)

export const getTokenBySymbol = (symbol: string, tokens = XRPL_TOKENS) =>
  tokens.find((token) => token.symbol === symbol)

const normalizeXrpToken = (token: XrplCurrency) => {
  if (!token) return token
  if (token.isNative || token.currency === 'XRP') {
    return {
      ...token,
      currency: 'XRP',
      symbol: token.symbol || 'XRP',
      name: token.name || 'XRP',
      decimals: Number.isFinite(token.decimals) ? token.decimals : 6,
      isNative: true,
      icon: DEFAULT_XRP_ICON,
    }
  }
  return token
}

const normalizeTokens = (tokens: XrplCurrency[]) => tokens.map(normalizeXrpToken)

const mergeTokens = (base: XrplCurrency[], extra: XrplCurrency[], preferExtra = false) => {
  const ordered = new Map<string, XrplCurrency>()
  base.forEach((token) => {
    ordered.set(getTokenKey(token), normalizeXrpToken(token))
  })
  extra.forEach((token) => {
    const normalized = normalizeXrpToken(token)
    const key = getTokenKey(normalized)
    if (!ordered.has(key)) {
      ordered.set(key, normalized)
      return
    }
    if (preferExtra) {
      const existing = ordered.get(key) || {}
      const next = normalizeXrpToken({ ...existing, ...normalized })
      if (!next.icon) {
        next.icon = existing.icon
      }
      ordered.set(key, next)
    }
  })
  return Array.from(ordered.values())
}

let cachedTokens: XrplCurrency[] | null = null
let inFlight: Promise<XrplCurrency[]> | null = null
let customTokens: XrplCurrency[] = loadCustomTokens()
const listeners = new Set<(tokens: XrplCurrency[]) => void>()

const getMergedTokens = (fetched: XrplCurrency[] = []) =>
  mergeTokens(mergeTokens(XRPL_TOKENS, fetched), customTokens, true)

const notifyListeners = (fetched: XrplCurrency[] = cachedTokens || []) => {
  const merged = getMergedTokens(fetched)
  listeners.forEach((listener) => listener(merged))
}

export const addCustomTokens = (tokens: XrplCurrency[]) => {
  const filtered = tokens.filter((token) => token && (token.isNative || token.currency === 'XRP' || Boolean(token.issuer)))
  if (!filtered.length) return
  const merged = mergeTokens(customTokens, filtered, true)
  if (JSON.stringify(merged) === JSON.stringify(customTokens)) return
  customTokens = merged
  saveCustomTokens(customTokens)
  notifyListeners()
}

export const useXrplTokens = () => {
  const [tokens, setTokens] = useState<XrplCurrency[]>(getMergedTokens(cachedTokens || []))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const retryRef = useRef<number | null>(null)

  useEffect(() => {
    const listener = (nextTokens: XrplCurrency[]) => {
      setTokens(nextTokens)
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    const loadTokens = async () => {
      if (!isMounted) {
        return
      }
      if (!BITHOMP_API_BASE) {
        return
      }
      if (cachedTokens) {
        setTokens(mergeTokens(XRPL_TOKENS, cachedTokens))
        return
      }
      setLoading(true)
      try {
        if (!inFlight) {
          inFlight = fetchBithompTokens()
        }
        const fetched = await inFlight
        cachedTokens = fetched
        if (isMounted) {
          notifyListeners(fetched)
        }
      } catch (fetchError) {
        if (isMounted) {
          const message =
            fetchError instanceof Error ? fetchError.message : 'Failed to load token list'
          setError(message || 'Failed to load token list')
          notifyListeners([])
          if (retryRef.current === null && typeof window !== 'undefined') {
            retryRef.current = window.setTimeout(() => {
              retryRef.current = null
              loadTokens()
            }, 5000)
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
        inFlight = null
      }
    }

    loadTokens()

    return () => {
      isMounted = false
      if (retryRef.current !== null && typeof window !== 'undefined') {
        window.clearTimeout(retryRef.current)
        retryRef.current = null
      }
    }
  }, [])

  return { tokens, loading, error }
}
